import { Fragment, useEffect, useState } from 'react'
import { ChromePicker, ColorResult } from 'react-color'
import { useController, UseControllerProps } from 'react-hook-form'

import { Dialog, Transition } from '@headlessui/react'
import { ExclamationCircleIcon } from '@heroicons/react/20/solid'
import { LockClosedIcon, XMarkIcon } from '@heroicons/react/24/outline'

import Button from '../misc/Button'

interface InputColorProps {
  label?: string
  defaultValue?: string
  disabled?: boolean
}

const InputColor = (props: UseControllerProps & InputColorProps) => {
  const { label, defaultValue, disabled } = props
  const { field, fieldState } = useController(props)

  const generateRandomColor = () => {
    return '#' + Math.floor(Math.random() * 16777215).toString(16)
  }

  const [color, setColor] = useState<string>(
    defaultValue || generateRandomColor()
  )
  const [showColorPicker, setShowColorPicker] = useState<boolean>(false)

  useEffect(() => {
    field.onChange(color)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color])

  return (
    <div className="w-full">
      {label && (
        <label
          className={`block text-sm font-medium ${disabled ? 'text-gray-400' : 'text-gray-700'}`}
          htmlFor={label}
        >
          <span className="inline-flex items-center">
            {label}
            {disabled && <LockClosedIcon className="h-4 px-2" />}
          </span>
        </label>
      )}
      <div className="relative mt-1">
        <div
          className="h-10 w-full cursor-pointer rounded-md border-2 border-gray-400"
          onClick={() => setShowColorPicker(!showColorPicker)}
          style={{ backgroundColor: color }}
        />

        {showColorPicker && (
          <Transition.Root as={Fragment} show={showColorPicker}>
            <Dialog
              as="div"
              className="relative z-40 h-screen w-screen"
              onClose={() => setShowColorPicker(false)}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
              </Transition.Child>

              <div className="fixed inset-0 z-30 overflow-y-auto scrollbar-thin">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  >
                    <Dialog.Panel
                      className={
                        'relative transform overflow-y-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left ' +
                        'shadow-xl transition-all sm:my-8'
                      }
                    >
                      <div className="fixed right-0 top-1 mb-3 p-3 sm:block">
                        <button
                          className="rounded-md bg-white text-gray-400 focus:outline-none hover:text-gray-500"
                          onClick={() => setShowColorPicker(false)}
                          type="button"
                        >
                          <span className="sr-only">Close</span>
                          <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                        </button>
                      </div>
                      <div className="pt-5">
                        <ChromePicker
                          color={color}
                          onChange={(colorResult: ColorResult) =>
                            setColor(colorResult.hex)
                          }
                        />
                      </div>
                      <div className="mt-3 flex justify-end">
                        <Button
                          action={() => setShowColorPicker(false)}
                          color="slate-600"
                          name="Aceptar"
                        />
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition.Root>
        )}

        {fieldState.error && (
          <>
            <div className="pointer-events-none absolute inset-y-0 right-0 top-2 pr-3">
              <ExclamationCircleIcon
                aria-hidden="true"
                className="h-5 w-5 text-red-500"
              />
            </div>

            <p className="absolute -bottom-5 flex flex-shrink-0 text-xs text-red-600">
              {fieldState.error?.message}
            </p>
          </>
        )}
      </div>
    </div>
  )
}

export default InputColor
